import { useAuth } from "@shared/hooks/useAuth";
import { WelcomeDialog } from "./WelcomeDialog";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { appConfig } from "@shared/utils/appConfig";
import { useSearchParams } from "react-router-dom";
import { useChatLogic } from "@features/Chat/useChatLogic";
import { threadAutoInteractionConfig } from "@features/Chat/chat-thread-auto-interaction-config";
import { appLogger } from "@shared/services/app-logger";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { useUpdateUserCommunicationPreferences } from "@queries/profile/useUpdateUserCommunicationPreferences";
import { useGuidedTourLogic } from "@features/GuidedTours/useGuidedTourLogic";

const WELCOME_FEATURE_START_DATE = new Date("2025-01-01");

export const WelcomeWrapper = () => {
  const { user, isValidatingCredentials } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const hasMlsAccessModule = useHasModule(sidekickModuleEnum.MLS_ACCESS);

  const navigateToThread = useCallback(
    (threadId: string | null) => {
      navigate(`/chat/${threadId}`);
    },
    [navigate],
  );

  const { handleSendMessage, isSendingMessage } = useChatLogic({
    navigateToThread,
  });

  const [hasConfirmedWelcome, setHasConfirmedWelcome] = useState(false);
  const {
    mutateAsync: updateCommunicationPreference,
    isPending: isUpdatingCommunicationPreference,
  } = useUpdateUserCommunicationPreferences();
  const { scheduleTour } = useGuidedTourLogic();

  const shouldForceWelcome = useMemo(() => {
    const welcome = search.get("welcome") === "true";
    return welcome && appConfig.APP_ENV !== "production";
  }, [search]);

  const onConfirmCompletedWelcome = useCallback(async () => {
    try {
      await updateCommunicationPreference({
        type: "onboardingFlow",
        enabled: true,
      });
    } catch (error) {
      appLogger.error("Error saving onboarding flow preference", { error });
      return;
    }
    setHasConfirmedWelcome(true);
    scheduleTour();
    try {
      await handleSendMessage({
        message: threadAutoInteractionConfig["welcome"].instructions,
        threadTitle: "Welcome to Sidekick",
        hidden: true,
        metadata: {
          flowType: "welcome",
        },
      });
    } catch (error) {
      appLogger.error("Error trying to start welcome thread flow", { error });
    }
  }, [handleSendMessage, updateCommunicationPreference, scheduleTour]);

  const isEligibleForWelcomeFeature = useMemo(
    () =>
      user.subscriptionStatus === "active" &&
      user.createdOn.getTime() > WELCOME_FEATURE_START_DATE.getTime(),
    [user],
  );

  const hasCompletedOnboardingFlow =
    user.communicationPreferences.onboardingFlow?.isEnabled ?? false;

  const open = useMemo(
    () =>
      !hasConfirmedWelcome &&
      hasMlsAccessModule &&
      ((!hasCompletedOnboardingFlow && isEligibleForWelcomeFeature) ||
        shouldForceWelcome),
    [
      hasConfirmedWelcome,
      hasMlsAccessModule,
      hasCompletedOnboardingFlow,
      isEligibleForWelcomeFeature,
      shouldForceWelcome,
    ],
  );

  if (isValidatingCredentials) {
    return null;
  }

  return (
    <WelcomeDialog
      open={open}
      onConfirmCompletedWelcome={onConfirmCompletedWelcome}
      userFirstName={user?.firstName}
      isLoading={isSendingMessage || isUpdatingCommunicationPreference}
    />
  );
};
